// Bundled twice from this source: synchronously in <head>, and with the app.
// The document owns one controller so both entries share listeners and session state.
export type ThemePref = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";
export interface ThemeState { theme: ResolvedTheme; pref: ThemePref }
export interface ThemeController {
  state(): ThemeState;
  choose(pref: ThemePref): void;
}

type ThemeWindow = Window & { __pwTheme?: ThemeController };

export function themePref(value: unknown): ThemePref {
  return value === "light" || value === "dark" ? value : "system";
}

/** Safe to call from the prepaint entry, React, or the standalone mock. */
export function initTheme(win: Window = window): ThemeController {
  const host = win as ThemeWindow;
  if (host.__pwTheme) return host.__pwTheme;
  const root = win.document.documentElement;
  const media = win.matchMedia?.("(prefers-color-scheme: dark)");
  let pref: ThemePref = "system";
  let sessionOnly = false;
  const saved = (): ThemePref => {
    try { return themePref(win.localStorage.getItem("pw-theme")); }
    catch { return "system"; }
  };
  const state = (): ThemeState => ({ pref, theme: pref === "system" ? (media?.matches ? "dark" : "light") : pref });
  const apply = () => {
    const next = state();
    root.setAttribute("data-pw-theme", next.theme);
    root.setAttribute("data-pw-theme-pref", next.pref);
    root.style.colorScheme = next.theme;
    const meta = win.document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute("content", next.theme === "dark" ? "#101D28" : "#FFFFFF");
    const event = win.document.createEvent("CustomEvent");
    event.initCustomEvent("pw-theme-change", false, false, next);
    win.dispatchEvent(event);
  };
  const controller: ThemeController = {
    state,
    choose(next) {
      pref = themePref(next);
      try { win.localStorage.setItem("pw-theme", pref); sessionOnly = false; }
      catch { sessionOnly = true; }
      apply();
    },
  };
  host.__pwTheme = controller;
  pref = saved();
  const follow = () => { if (pref === "system") apply(); };
  media?.addEventListener("change", follow);
  win.addEventListener("storage", (event: StorageEvent) => {
    if (event.key !== "pw-theme" && event.key !== null) return;
    sessionOnly = false;
    pref = saved();
    apply();
  });
  win.addEventListener("pageshow", () => {
    if (!sessionOnly) pref = saved();
    apply();
  });
  apply();
  return controller;
}
